import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5678';
const CACHE_KEY = 'rag_error_codes_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function loadFromCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function saveToCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // localStorage unavailable — degrade gracefully
  }
}

// Client-side classify patterns (mirrors errorCodeService.classify on the server).
// Used only for raw rag_error strings coming from the DB before Plan B adds error_code column.
//
// EL ORDEN ES LA SEMÁNTICA: se devuelve el PRIMER patrón que casa, así que un patrón
// genérico colocado arriba se come a los específicos de abajo. Cualquier añadido va
// pensando en eso, no al final por comodidad (#581).
const RAW_CLASSIFY_PATTERNS = [
  { pattern: /magic.?byte|extension.*mismatch|mismatch.*extension/i, code: 'MAGIC_BYTE_MISMATCH' },
  { pattern: /password.?protect|is encrypted|requires password|password required|contrase/i, code: 'EXTRACT_FAILED_PASSWORD_PROTECTED' },
  { pattern: /corrupt|invalid.*file|damaged|malformed|dañado/i, code: 'EXTRACT_FAILED_CORRUPT' },
  { pattern: /too many images|exceeded.*image|image.*limit|demasiadas im/i, code: 'EXTRACT_FAILED_TOO_MANY_IMAGES' },
  // #581 — TIENE QUE IR POR ENCIMA de los dos siguientes, que si no lo secuestran:
  //   · «imágenes sin describir» no casa hoy con nada, pero el mensaje enriquecido lleva el
  //     motivo pegado, y «formato de imagen no soportado» CASA con `no soportado` del patrón
  //     de UNSUPPORTED_TYPE → diría «el tipo de fichero no está soportado» de un .docx
  //     perfectamente soportado cuyas imágenes no se pudieron describir;
  //   · un fichero-imagen cuyo caption falla llega hoy sin mensaje y acaba en
  //     EMPTY_CONTENT («no contiene texto procesable», permanent) — el fallo medido en prod.
  { pattern: /im[áa]gen(es)?\s+sin\s+describir|no se pudo describir la imagen|image caption failed/i, code: 'IMG_CAPTION_FAILED' },
  { pattern: /0 chunks|no text|empty content|no content|sin contenido/i, code: 'EXTRACT_FAILED_EMPTY_CONTENT' },
  { pattern: /unsupported.*type|cannot process|not supported|no soportado/i, code: 'EXTRACT_FAILED_UNSUPPORTED_TYPE' },
  { pattern: /rate.?limit|429|too many requests/i, code: 'EMBED_FAILED_RATE_LIMIT' },
  { pattern: /econnrefused|econnreset|network.*error/i, code: 'BUCKET_DOWNLOAD_FAILED_NETWORK' },
  { pattern: /timeout|timed out|tardó demasiado/i, code: 'TIMEOUT' },
  // #699 — «Completed with N failed batches» es el literal de `Aggregate Results`
  // (H__BatchEmbeddings) y llega tal cual a la BD. VA DESPUÉS de TIMEOUT a propósito: cuando
  // #698 recupere la causa del lote, el mensaje llevará «timeout» y ese veredicto es mejor que
  // éste. El código es el mismo que concluye `Classify Error` para un lote fallido sin más
  // pistas, y es transient/retryable: sin este patrón el fichero de Kafrein caía a «error
  // desconocido» y perdía el botón de reintento que un fallo transitorio sí merece.
  { pattern: /failed batch/i, code: 'EMBED_FAILED_PROVIDER_ERROR' },
];

// #699 — el agregado de captioning SUBORDINADO no es la causa del fallo, así que no puede
// decidir el código. El backend lo pega con este marcador exacto (`enrichErrorWithCause` en
// `server/services/rag/imageWarningText.js`) precisamente para que se distinga de la forma
// CAUSAL, que sí debe clasificar como IMG_CAPTION_FAILED. Sin este recorte, un timeout de la
// vectorización con 340 imágenes sin describir se anunciaba como «No se pudo describir la
// imagen» —el error de verdad quedaba sólo en el tooltip— y heredaba su reintentabilidad
// (medido en noprod el 2026-08-26, presa Kafrein Dam).
const SUBORDINATED_CAPTION_TAIL = /\s·\s*además, im[áa]genes sin describir:.*$/i;

// #1416 — espejo de LIST_FOLDER_PREFIX/LIST_FOLDER_PATTERNS de `errorClassifier`: el fallo al
// LISTAR una carpeta no es de ningún fichero y no pasa por los patrones de arriba.
const LIST_FOLDER_PREFIX = /failed to list files\b/i;
const LIST_FOLDER_PATTERNS = [
  { pattern: /not found|could not be found|itemnotfound/i, code: 'LIST_FAILED_FOLDER_NOT_FOUND' },
  { pattern: /graph.*401|401.*graph|token expired|token.*revoked|authentication.*failed|bearer.*invalid/i, code: 'STAGE_FAILED_GRAPH_AUTH' },
  { pattern: /timeout|timed out/i, code: 'TIMEOUT' },
];

function classifyRaw(rawError) {
  const msg = String(rawError).replace(SUBORDINATED_CAPTION_TAIL, '');
  if (LIST_FOLDER_PREFIX.test(msg)) {
    for (const { pattern, code } of LIST_FOLDER_PATTERNS) {
      if (pattern.test(msg)) return code;
    }
    return 'LIST_FAILED';
  }
  for (const { pattern, code } of RAW_CLASSIFY_PATTERNS) {
    if (pattern.test(msg)) return code;
  }
  return null; // no match → fall back to raw string
}

// Sparse fallback map for the most actionable codes, used when the fetch fails
const FALLBACK_MESSAGES = {
  MAGIC_BYTE_MISMATCH: { es: 'La extensión del fichero no coincide con su contenido real.', action: 'reupload', retryable: false },
  EXTRACT_FAILED_PASSWORD_PROTECTED: { es: 'El fichero está protegido con contraseña.', action: 'reupload', retryable: false },
  EXTRACT_FAILED_CORRUPT: { es: 'El fichero parece estar dañado.', action: 'reupload', retryable: false },
  // #581 — el texto enuncia el hecho y no recomienda reintentar: esa decisión es del
  // usuario. `retryable:true` NO es texto, es lo que habilita el botón de reintento que ya
  // existía; sirve justamente para que el usuario PUEDA decidir (con `false` no hay opción
  // que ofrecerle). Espeja `rag_error_codes.is_retryable` para el caso de que falle el fetch.
  IMG_CAPTION_FAILED: { es: 'No se pudo describir la imagen.', action: 'retry', retryable: true },
  PIPELINE_FAILED_NO_MESSAGE: { es: 'El procesado falló sin informar del motivo.', action: 'retry', retryable: true },
  // #1416 — códigos nuevos: un catálogo cacheado antes de la migración (24 h) no los trae, y
  // sin estas dos entradas la tarjeta de la carpeta diría «Error procesando el fichero.».
  LIST_FAILED_FOLDER_NOT_FOUND: { es: 'No se encuentra la carpeta en SharePoint: se ha borrado o movido.', action: null, retryable: false },
  LIST_FAILED: { es: 'No se pudo leer la carpeta en SharePoint. Reintenta.', action: 'retry', retryable: true },
};
const FALLBACK_DEFAULT = { es: 'Error procesando el fichero.', action: null, retryable: false };

export function useRagErrorCodes() {
  const { session } = useAuth();
  const authToken = session?.access_token;

  const [codes, setCodes] = useState(() => loadFromCache() || []);

  useEffect(() => {
    if (!authToken) return;
    if (loadFromCache()) return; // still fresh

    fetch(`${API_BASE_URL}/api/rag/error-codes`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(res => res.ok ? res.json() : Promise.reject(res.status))
      .then(data => {
        saveToCache(data);
        setCodes(data);
      })
      .catch(() => {
        // Keep whatever is in state (cache or empty) — non-fatal
      });
  }, [authToken]);

  const getEntry = useCallback((code) => {
    return codes.find(c => c.code === code) || null;
  }, [codes]);

  const getMessage = useCallback((code, locale = 'es') => {
    const entry = getEntry(code);
    if (entry) return locale === 'en' ? entry.user_message_en : entry.user_message_es;
    const fallback = FALLBACK_MESSAGES[code];
    if (fallback) return fallback.es;
    return FALLBACK_DEFAULT.es;
  }, [getEntry]);

  const getAction = useCallback((code) => {
    const entry = getEntry(code);
    if (entry) return entry.suggested_action;
    return FALLBACK_MESSAGES[code]?.action ?? FALLBACK_DEFAULT.action;
  }, [getEntry]);

  const isRetryable = useCallback((code) => {
    const entry = getEntry(code);
    if (entry) return entry.is_retryable;
    return FALLBACK_MESSAGES[code]?.retryable ?? FALLBACK_DEFAULT.retryable;
  }, [getEntry]);

  // Translates a raw rag_error string (DB value) to a user-friendly message.
  // Classifies the raw string client-side to find a taxonomy code, then delegates
  // to getMessage. Falls back to the raw string if no pattern matches.
  // Remove in Plan B once DB carries an error_code column.
  //
  // `explicitCode` (#581): un código que ya viene etiquetado en origen GANA sobre los
  // patrones. Adivinar el código a partir del texto es una heurística; un código puesto por
  // quien sabe qué pasó no lo es, así que no se le pisa nunca.
  //
  // ⚠ HOY NADIE LO PASA, y no es un olvido: NINGÚN camino por fichero lleva código hasta el
  // cliente. `document_sync_state` no tiene columna de código —y en la vía del fichero suelto
  // el código de `Classify Error` no se persiste en ninguna parte: sólo se usa en vuelo, para
  // que `enrichErrorWithCause` decida si el agregado de captioning es causa o contexto—.
  // `rag_job_status.error_code` SÍ sale por la API, pero no por la ruta que este cliente usa:
  // `getJob`/`getJobForUser` hacen `SELECT *` y `GET /api/rag-jobs/:jobId` devuelve la fila
  // entera; el frontend llama a `/api/rag-jobs/active`, que filtra
  // `status IN ('pending','processing')` y por eso nunca devuelve un job fallido — el único que
  // llevaría código. El parámetro existe para que enchufarlo sea una línea el día que se
  // exponga, no para adivinar mejor hoy.
  //
  // ⚠ CORREGIDO 2026-09-03 (#698): este bloque decía «se ESCRIBE y no se lee: ningún SELECT del
  // backend lo devuelve (verificado 2026-08-25)». Es falso: el `SELECT *` lo devuelve. La frase
  // venía del cuerpo de #698, así que el error viajó del issue al código y aquí ganó un
  // «verificado» que no tenía. El desenlace práctico no cambia —a este hook no llega—, pero la
  // causa sí, y con ella el arreglo: el hueco está en el consumidor y en la ruta que se llama,
  // no en la consulta.
  const getMessageFromRaw = useCallback((rawError, locale = 'es', explicitCode = null) => {
    if (explicitCode) return getMessage(explicitCode, locale);
    if (!rawError || typeof rawError !== 'string') return FALLBACK_DEFAULT.es;
    const trimmed = rawError.trim();
    if (!trimmed) return FALLBACK_DEFAULT.es;
    const code = classifyRaw(trimmed);
    if (code) return getMessage(code, locale);
    return trimmed; // unknown pattern — show raw error as last resort
  }, [getMessage]);

  // Resolves all UX properties for a raw error string in a single call.
  // Returns { message, action, retryable }.
  // `explicitCode` manda sobre los patrones — ver la nota de getMessageFromRaw.
  const resolveFromRaw = useCallback((rawError, locale = 'es', explicitCode = null) => {
    const resolveByCode = (code) => ({
      message: getMessage(code, locale),
      action: getAction(code),
      retryable: isRetryable(code),
    });
    if (explicitCode) return resolveByCode(explicitCode);
    if (!rawError || typeof rawError !== 'string' || !rawError.trim()) {
      return { message: FALLBACK_DEFAULT.es, action: FALLBACK_DEFAULT.action, retryable: FALLBACK_DEFAULT.retryable };
    }
    const trimmed = rawError.trim();
    const code = classifyRaw(trimmed);
    if (code) return resolveByCode(code);
    return { message: trimmed, action: FALLBACK_DEFAULT.action, retryable: FALLBACK_DEFAULT.retryable };
  }, [getMessage, getAction, isRetryable]);

  return { getMessage, getAction, isRetryable, getMessageFromRaw, resolveFromRaw };
}
